import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { InputFormFieldComponent } from '../forms/fields/input-form-field/input-form-field.component';
import { CheckboxFormFieldComponent } from '../forms/fields/checkbox-form-field/checkbox-form-field.component';
import { DataTableColumnFilters, DataTableColumnFiltersApi, DataTableSortBooleanEnum, DataTableStateModel } from '../dynamic-table/dynamic-table.model';
import { DaterangepickerFormFieldComponent } from '../forms/fields/daterangepicker-form-field/daterangepicker-form-field.component';
import { FiltersService } from 'app/shared/services/filters.service';
import { debounceTime, distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';
import { MultipleSelectionOptionsTypeEnum } from 'app/shared/enums/datatable-names.enum';

@Component({
  selector: 'app-dynamic-table-filter',
  standalone: true,
  imports: [
    RouterModule,
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    InputFormFieldComponent,
    CheckboxFormFieldComponent,
    DaterangepickerFormFieldComponent
  ],
  templateUrl: './dynamic-table-filter.component.html',
  styleUrl: './dynamic-table-filter.component.scss'
})
export class DynamicTableFilterComponent implements OnInit, AfterViewInit {
  @Input() filtersState!: DataTableColumnFiltersApi[] | undefined;
  @Input() datatableSettings!: DataTableStateModel | undefined;
  @Input() globalSearchTerm!: string;
  @Input() datatableFilterPosition: any = {};
  @Input() filterValues: DataTableColumnFilters[] = [];
  @Input() columnProp: string = '';
  @Input() displayName: string = '';
  @Input() currentFilter: DataTableColumnFiltersApi = {};
  @Input() showFilterSearch: boolean = false;
  @Input() columns: any[] = [];
  selectedFilterValues: string[] | undefined | null = [];

  @Output() onCloseFilter = new EventEmitter<void>();
  @Output() onApplyFilter = new EventEmitter<Object>();
  @Output() isFilterHasValue = new EventEmitter<Object>();

  inputSearchUpdate$ = new Subject<any>();
  private scroll$ = new Subject<Event>();
  private destroy$ = new Subject<void>();
  otherColumnsFilter: DataTableColumnFiltersApi[] = [];
  loading = false;
  hasMore = true;

  searchPagination = {
    term: '',
    page: 1,
    count: 50
  }

  defaultSearchPagination = { ... this.searchPagination };

  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private filtersService: FiltersService
  ) {
    this.inputSearchUpdate$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    )
      .subscribe(value => {
        this.searchPagination = { ...this.defaultSearchPagination };
        this.hasMore = true;
        this.searchPagination.term = value;
        this.updateFiltersOptions();
      });

    this.scroll$
      .pipe(
        debounceTime(80),
        filter((e) => {
          const el = e.target as HTMLElement;
          const threshold = 50;
          return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
        }),
        filter(() => !this.loading && this.hasMore),
      )
      .subscribe(() => {
        this.searchPagination.page++;
        this.updateFiltersOptions();
      });
  }

  ngOnInit(): void {
    this.filterValues = [];
    this.createForm();

    const thisColumnFilters = this.filtersState?.filter(f => f.columnName === this.columnProp) ?? [];
    this.otherColumnsFilter = this.filtersState?.filter(f => f.columnName !== this.columnProp) ?? [];

    if (thisColumnFilters[0]) {
      this.selectedFilterValues = thisColumnFilters[0].columnSelectedValues;
    }

    this.updateFiltersOptions();

  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      document.getElementById('app-input-form-field')?.focus();
    });
  }

  createForm() {
    this.form = this.fb.group({
      columnName: [this.columnProp],
      displayName: [this.displayName],
      columnSearchValue: [this.currentFilter?.columnSearchValue || ''],
      dateFilter: [
        this.isDateColumn() && this.currentFilter?.columnDateTimeRangeValues
          ? [
            this.currentFilter.columnDateTimeRangeValues.from
              ? new Date(this.currentFilter.columnDateTimeRangeValues.from)
              : null,
            this.currentFilter.columnDateTimeRangeValues.to
              ? new Date(this.currentFilter.columnDateTimeRangeValues.to)
              : null
          ]
          : [null, null]
      ]
    });
  }

  addCheckboxFields(values: DataTableColumnFilters[]) {
    values.forEach((element: DataTableColumnFilters) => {
      this.form.addControl(element.value, new FormControl(
        this.currentFilter?.columnSelectedValues?.find((item: string) => item === element.value) ? true : false
      ));
    });

    if (this.isBooleanColumn()) {
      setTimeout(() => {
        values.forEach((element: DataTableColumnFilters) => {
          const formControlName = element.value;
          const formControlValue = this.form.get(formControlName)?.value;
          if (formControlName && formControlValue) {
            this.onChangeCheckbox(formControlValue, formControlName);
          }
        });
      });
    }

  }

  updateFiltersOptions() {
    const payload = {
      multiSelectField: {
        columnName: this.columnProp,
        columnSearchValue: this.searchPagination.term ?? ''
      },
      fieldFilters: this.otherColumnsFilter,
      querySearch: this.globalSearchTerm,
      skipCount: (this.searchPagination.page - 1) * this.searchPagination.count,
      takeCount: this.searchPagination.count
    }

    if (this.isMultipleSelectionColumn()) {
      const gridName: string = this.datatableSettings?.gridName ?? '';
      this.loading = true;
      if (gridName === 'ProblemRecordDataTable' || gridName === 'SearchProblemRecordDataTable') {
        this.filterValues = this.columns.find(col => col.prop === this.columnProp)?.dataTableColumnFilters ?? [];
        this.addCheckboxFields(this.filterValues);
      } else
      {
        this.loading = true;
        this.filtersService.getColumnFilterItems(payload, this.datatableSettings?.tableType ?? '', MultipleSelectionOptionsTypeEnum[gridName as keyof typeof MultipleSelectionOptionsTypeEnum], this.globalSearchTerm).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {

        this.filterValues = this.searchPagination.page > 1 ? [...this.filterValues, ...res] : res;
        this.hasMore = res.length >= this.searchPagination.count;

        if (res?.length) {
          this.addCheckboxFields(res);
        }
        this.loading = false;
      });
      }
    } else if (this.isBooleanColumn()) {
      this.filterValues = [{ name: 'Yes', value: DataTableSortBooleanEnum.TRUE }, { name: 'No', value: DataTableSortBooleanEnum.FALSE }];
      this.addCheckboxFields(this.filterValues);
    }
  }

  addSelection(value: string) {
    if (this.form.get(value)?.value) {
      this.selectedFilterValues?.push(value);
    } else {
      this.removeSelection(value);
    }
  }

  removeSelection(value: string) {
    this.selectedFilterValues = this.selectedFilterValues?.filter(item => item !== value);
    this.form.get(value)?.setValue(false);
    this.form.updateValueAndValidity();
  }

  submitForm() {
    const value = this.form.getRawValue();

    let dateRange = { from: null, to: null };
    if (Array.isArray(value.dateFilter) && value.dateFilter.length === 2) {
      const [from, to] = value.dateFilter;
      dateRange = {
        from: from ? new Date(new Date(from).setHours(0, 0, 0, 0)).toISOString() : null,
        to: to ? new Date(new Date(to).setHours(23, 59, 59, 0)).toISOString() : null
      } as any;
    }

    const currentFilter = {
      columnName: value.columnName,
      displayName: value.displayName,
      columnSearchValue: this.isMultipleSelectionColumn() ? null : value.columnSearchValue || null,
      columnSelectedValues: this.isMultipleSelectionColumn() ? this.selectedFilterValues : this.prepareActiveCheckboxValues(value),
      columnDateTimeRangeValues: dateRange
    }

    this.isFilterHasValue.emit({
      columnName: value.columnName,
      isColumnFilterActive: !!(currentFilter && (currentFilter.columnSearchValue || (currentFilter.columnSelectedValues && currentFilter.columnSelectedValues.length > 0) ||
        (currentFilter.columnDateTimeRangeValues &&
          (currentFilter.columnDateTimeRangeValues.from || currentFilter.columnDateTimeRangeValues.to))))
    });

    this.onApplyFilter.emit(currentFilter);
  }

  prepareActiveCheckboxValues(formValues: any): string[] {
    const activeCheckboxValues: string[] = [];

    Object.keys(formValues).forEach(key => {
      if (formValues[key] === true) {
        activeCheckboxValues.push(key);
      }
    });

    return activeCheckboxValues;
  }

  closeFilter() {
    this.onCloseFilter.emit();
  }

  isDateColumn(): boolean {
    if (!this.columnProp) return false;
    const columnConfig = this.columns.find(col => col.prop === this.columnProp);
    return columnConfig?.fieldType === 'date' || columnConfig?.fieldType === 'datetime';
  }

  isBooleanColumn(): boolean {
    if (!this.columnProp) return false;
    const columnConfig = this.columns.find(col => col.prop === this.columnProp);
    return columnConfig?.fieldType === 'boolean';
  }

  isMultipleSelectionColumn(): boolean {
    return !(this.isBooleanColumn() || this.isDateColumn()) && !this.showFilterSearch
  }

  onEnter(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.submitForm();
    }
  }

  onChangeCheckbox(value: boolean, itemName: string) {
    switch (itemName) {
      case DataTableSortBooleanEnum.TRUE:
        value ? this.form.controls[DataTableSortBooleanEnum.FALSE].disable() : this.form.controls[DataTableSortBooleanEnum.FALSE].enable();
        this.form.get(DataTableSortBooleanEnum.FALSE)?.updateValueAndValidity();
        break;
      case DataTableSortBooleanEnum.FALSE:
        value ? this.form.controls[DataTableSortBooleanEnum.TRUE].disable() : this.form.controls[DataTableSortBooleanEnum.TRUE].enable();
        this.form.get(DataTableSortBooleanEnum.TRUE)?.updateValueAndValidity();
        break;
    }
  }

  onScroll(e: Event) {
    this.scroll$.next(e);
  }

}
